"use client";

/**
 * Flash Sale 목록 페이지
 * ✅ curl verified — 실제 API 응답 구조 기반
 */
import Image from "next/image";
import Link from "next/link";
import { Clock, Package } from "lucide-react";

import { cn } from "@/lib/utils";

import {
  useFlashSales,
  type FlashSaleGroupWithStocks,
} from "@/features/flash-sale/queries/useFlashSale";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";

function FlashSaleCardSkeleton() {
  return (
    <div className="rounded-md border overflow-hidden">
      <Skeleton className="aspect-[4/3] w-full" />
      <div className="p-4 space-y-2">
        <Skeleton className="h-5 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    </div>
  );
}

function FlashSaleCard({ group }: { group: FlashSaleGroupWithStocks }) {
  const isEnded = group.status === "ended" || group.status === "sold";
  const firstImage = group.returnedStocks?.[0]?.imagePath;

  const content = (
    <div
      className={cn(
        "rounded-md border overflow-hidden transition-shadow hover:shadow-md",
        isEnded && "opacity-60",
      )}
    >
      <div className="relative aspect-[4/3] bg-neutral-100 overflow-hidden">
        {firstImage ? (
          <Image
            src={firstImage}
            alt={group.brandName}
            fill
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
            className="object-cover"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center">
            <Package className="h-10 w-10 text-neutral-300" />
          </div>
        )}
        {Number(group.discount_rate) > 0 && (
          <span className="absolute top-2 left-2 bg-neutral-900 text-white text-xs font-medium px-2 py-0.5 rounded-sm">
            {Math.round(Number(group.discount_rate))}% OFF
          </span>
        )}
        {isEnded && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="text-white font-semibold text-lg tracking-wider">SOLD OUT</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-2">
        {/* Product name from first stock */}
        <h3 className="font-medium text-[13px] leading-[1.4] line-clamp-2">
          {group.returnedStocks?.[0]?.productName || group.brandName}
          {group.returnedStocks?.length > 1 && ` / ${group.returnedStocks.length}개 set`}
        </h3>
        {/* Price with discount */}
        {group.returnedStocks?.length > 0 &&
          (() => {
            const totalOriginal = group.returnedStocks.reduce(
              (s, st) => s + st.price * st.amount,
              0,
            );
            const rate = Number(group.discount_rate);
            const discounted = Math.floor(totalOriginal * (1 - rate / 100));
            return (
              <div className="flex items-baseline gap-2">
                <span className="text-xs text-neutral-400 line-through">
                  {totalOriginal.toLocaleString()}원
                </span>
                <span className="text-sm font-semibold">{discounted.toLocaleString()}원</span>
                {rate > 0 && (
                  <span className="text-xs font-medium text-red-500">
                    ({Math.round(rate)}% 할인)
                  </span>
                )}
              </div>
            );
          })()}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" />
          <span>
            {new Date(group.start_date).toLocaleDateString("ko-KR")} ~{" "}
            {new Date(group.end_date).toLocaleDateString("ko-KR")}
          </span>
        </div>
      </div>
    </div>
  );

  if (isEnded) return content;

  return <Link href={`/flash-sale/${group.slug}`}>{content}</Link>;
}

function compareLatestFlashSale(a: FlashSaleGroupWithStocks, b: FlashSaleGroupWithStocks) {
  const createdDiff = new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  if (createdDiff !== 0) return createdDiff;
  return b.id - a.id;
}

export function FlashSaleListContent() {
  const { data, isLoading, isError } = useFlashSales();
  const groups: FlashSaleGroupWithStocks[] = data ?? [];
  const activeGroups = groups
    .filter((g) => g.status !== "ended" && g.status !== "sold")
    .sort(compareLatestFlashSale);
  const endedGroups = groups.filter((g) => g.status === "ended" || g.status === "sold");

  return (
    <div className="max-w-screen-xl mx-auto px-4 py-8">
      <h1 className="text-xl font-medium mb-6">깜짝 할인</h1>

      {/* 통째 구매 영역은 임시 숨김. FlashSaleBulkOffer 컴포넌트는 복구할 수 있도록 보존한다. */}

      <div className="bg-neutral-50 border border-neutral-200 rounded-md px-5 py-4 mb-8 text-sm text-muted-foreground space-y-1">
        <p className="font-medium text-foreground">구매 전 확인사항</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>반품 재고 기반 한정 수량 상품입니다.</li>
          <li>세트는 개별로 구매할 수 있습니다.</li>
          <li>다른 상품과 합산 결제는 불가합니다.</li>
          <li>구매 확정 후 취소/교환/반품이 제한됩니다.</li>
        </ul>
      </div>

      {isLoading && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {Array.from({ length: 6 }).map((_, i) => (
            <FlashSaleCardSkeleton key={i} />
          ))}
        </div>
      )}

      {isError && (
        <div className="flex flex-col items-center gap-6 text-center pt-32 pb-20">
          <div className="relative">
            <div className="absolute -inset-4 bg-neutral-100 rounded-full blur-xl" />
            <Package className="relative h-14 w-14 text-neutral-300" strokeWidth={1} />
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium">깜짝 할인 정보를 불러올 수 없습니다</p>
            <p className="text-sm text-muted-foreground">잠시 후 다시 시도해주세요.</p>
          </div>
          <Button
            variant="outline"
            size="sm"
            className="rounded-sm"
            onClick={() => window.location.reload()}
          >
            다시 시도
          </Button>
        </div>
      )}

      {!isLoading && !isError && groups.length === 0 && (
        <div className="flex flex-col items-center gap-6 text-center pt-32 pb-20">
          <div className="relative">
            <div className="absolute -inset-4 bg-neutral-50 rounded-full blur-xl" />
            <Package className="relative h-14 w-14 text-neutral-200" strokeWidth={1} />
          </div>
          <div className="space-y-2">
            <p className="text-lg font-medium">현재 진행 중인 깜짝 할인이 없습니다</p>
            <p className="text-sm text-muted-foreground">새로운 할인 이벤트를 기다려주세요.</p>
          </div>
          <Button variant="outline" size="sm" className="rounded-sm" asChild>
            <Link href="/shopping">상품 둘러보기</Link>
          </Button>
        </div>
      )}

      {activeGroups.length > 0 && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
          {activeGroups.map((group) => (
            <FlashSaleCard key={group.id} group={group} />
          ))}
        </div>
      )}

      {endedGroups.length > 0 && (
        <div className="mt-12">
          <h2 className="text-base font-medium text-muted-foreground mb-4">종료된 할인</h2>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
            {endedGroups.map((group) => (
              <FlashSaleCard key={group.id} group={group} />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
